import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as api from '../../services/api';

type Tab = 'ask' | 'rehab';

interface AdminAgentProps {
  embedded?: boolean;
}

const Agent: React.FC<AdminAgentProps> = ({ embedded = false }) => {
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>('rehab');

  // Ask state
  const [question, setQuestion] = useState('');
  const [askResult, setAskResult] = useState<{ answer: string; sources?: any[] } | null>(null);
  const [askLoading, setAskLoading] = useState(false);
  const [askErr, setAskErr] = useState('');

  // Rehab state
  const [patientSummary, setPatientSummary] = useState('');
  const [age, setAge] = useState('');
  const [sport, setSport] = useState('');
  const [symptoms, setSymptoms] = useState('');
  const [issues, setIssues] = useState('');
  const [preliminaryDx, setPreliminaryDx] = useState('');
  const [extraNotes, setExtraNotes] = useState('');
  const [rehabResult, setRehabResult] = useState<{ plan: string; sources?: any[] } | null>(null);
  const [rehabLoading, setRehabLoading] = useState(false);
  const [rehabErr, setRehabErr] = useState('');

  const onAsk = () => {
    if (!question.trim()) return;
    setAskErr('');
    setAskResult(null);
    setAskLoading(true);
    api.agentAsk(question.trim())
      .then((res) => setAskResult({ answer: res.answer, sources: res.sources }))
      .catch((e) => setAskErr(e?.message || 'Ask failed'))
      .finally(() => setAskLoading(false));
  };

  const onRehabPlan = () => {
    setRehabErr('');
    setRehabResult(null);
    setRehabLoading(true);
    api.agentRehabPlan({
      patient_summary: patientSummary.trim() || undefined,
      age: age.trim() ? parseInt(age, 10) : undefined,
      sport: sport.trim() || undefined,
      symptoms: symptoms.trim() || undefined,
      issues: issues.trim() || undefined,
      preliminary_diagnosis: preliminaryDx.trim() || undefined,
      extra_notes: extraNotes.trim() || undefined,
    })
      .then((res) => setRehabResult({ plan: res.plan, sources: res.sources }))
      .catch((e) => setRehabErr(e?.message || 'Rehab plan failed'))
      .finally(() => setRehabLoading(false));
  };

  return (
    <div style={{ padding: embedded ? 0 : 24, maxWidth: 900 }}>
      {!embedded && (
        <button type="button" onClick={() => navigate('/admin/portal')} style={{ marginBottom: 16 }}>
          Back to home
        </button>
      )}
      {!embedded && <h1>Dr. Schmoe AI – Agent</h1>}
      <p style={{ color: '#666', marginBottom: 24 }}>
        Ask questions from Dr. Schmoe&apos;s notes or get a personalized rehab plan.
      </p>

      <div style={{ marginBottom: 16 }}>
        <button
          type="button"
          onClick={() => setTab('ask')}
          style={{ marginRight: 8, fontWeight: tab === 'ask' ? 'bold' : 'normal' }}
        >
          Ask a question
        </button>
        <button
          type="button"
          onClick={() => setTab('rehab')}
          style={{ fontWeight: tab === 'rehab' ? 'bold' : 'normal' }}
        >
          Rehab plan
        </button>
      </div>

      {tab === 'ask' && (
        <section style={{ marginBottom: 32 }}>
          <label style={{ display: 'block', marginBottom: 8 }}>
            Question
          </label>
          <textarea
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder="e.g. What does Dr. Schmoe recommend for vestibular rehab?"
            rows={3}
            style={{ width: '100%', padding: 8, marginBottom: 12 }}
          />
          <button type="button" onClick={onAsk} disabled={askLoading}>
            {askLoading ? 'Asking...' : 'Ask'}
          </button>
          {askErr && <div style={{ color: 'red', marginTop: 8 }}>{askErr}</div>}
          {askResult && (
            <div style={{ marginTop: 16 }}>
              <h3>Answer</h3>
              <div style={{ whiteSpace: 'pre-wrap', background: '#f5f5f5', padding: 16, borderRadius: 4 }}>
                {askResult.answer}
              </div>
              {askResult.sources && askResult.sources.length > 0 && (
                <div style={{ marginTop: 12, fontSize: 14, color: '#666' }}>
                  Sources: {askResult.sources.map((s: any) => `${s.source_file} (chunk ${s.chunk_index})`).join(', ')}
                </div>
              )}
            </div>
          )}
        </section>
      )}

      {tab === 'rehab' && (
        <section style={{ marginBottom: 32 }}>
          <p style={{ marginBottom: 16 }}>
            Paste a full patient case below, or fill in the structured fields. The AI will use Dr. Schmoe&apos;s notes to generate a personalized rehab plan.
          </p>
          <div style={{ marginBottom: 20 }}>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 600 }}>
              Patient case / full description (paste here)
            </label>
            <textarea
              value={patientSummary}
              onChange={(e) => setPatientSummary(e.target.value)}
              placeholder="Paste the full patient presentation, e.g. A 20-year-old college softball player presented with persistent dizziness, nausea, headaches..."
              rows={8}
              style={{ width: '100%', padding: 12, fontSize: 14, boxSizing: 'border-box' }}
            />
          </div>
          <details style={{ marginBottom: 16 }}>
            <summary style={{ cursor: 'pointer', color: '#666' }}>Optional: structured fields</summary>
            <div style={{ display: 'grid', gap: 12, marginTop: 12 }}>
            <div>
              <label style={{ display: 'block', marginBottom: 4 }}>Age (optional)</label>
              <input
                type="number"
                value={age}
                onChange={(e) => setAge(e.target.value)}
                placeholder="e.g. 21"
                style={{ width: '100%', padding: 8 }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: 4 }}>Sport / activity</label>
              <input
                type="text"
                value={sport}
                onChange={(e) => setSport(e.target.value)}
                placeholder="e.g. softball"
                style={{ width: '100%', padding: 8 }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: 4 }}>Symptoms</label>
              <textarea
                value={symptoms}
                onChange={(e) => setSymptoms(e.target.value)}
                placeholder="e.g. dizziness, headache with exertion"
                rows={2}
                style={{ width: '100%', padding: 8 }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: 4 }}>Issues</label>
              <textarea
                value={issues}
                onChange={(e) => setIssues(e.target.value)}
                placeholder="e.g. loss of balance, difficulty with running"
                rows={2}
                style={{ width: '100%', padding: 8 }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: 4 }}>Preliminary diagnosis</label>
              <input
                type="text"
                value={preliminaryDx}
                onChange={(e) => setPreliminaryDx(e.target.value)}
                placeholder="e.g. vestibular-ocular integration deficits"
                style={{ width: '100%', padding: 8 }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: 4 }}>Extra notes (optional)</label>
              <textarea
                value={extraNotes}
                onChange={(e) => setExtraNotes(e.target.value)}
                placeholder="Any other context"
                rows={2}
                style={{ width: '100%', padding: 8 }}
              />
            </div>
            </div>
          </details>
          <button type="button" onClick={onRehabPlan} disabled={rehabLoading || (!patientSummary.trim() && !symptoms.trim() && !issues.trim())}>
            {rehabLoading ? 'Generating plan...' : 'Get rehab plan'}
          </button>
          {rehabErr && <div style={{ color: 'red', marginTop: 8 }}>{rehabErr}</div>}
          {rehabResult && (
            <div style={{ marginTop: 16 }}>
              <h3>Rehab plan</h3>
              <div style={{ whiteSpace: 'pre-wrap', background: '#f5f5f5', padding: 16, borderRadius: 4 }}>
                {rehabResult.plan}
              </div>
              {rehabResult.sources && rehabResult.sources.length > 0 && (
                <div style={{ marginTop: 12, fontSize: 14, color: '#666' }}>
                  Sources: {rehabResult.sources.map((s: any) => `${s.source_file} (chunk ${s.chunk_index})`).join(', ')}
                </div>
              )}
            </div>
          )}
        </section>
      )}
    </div>
  );
};

export default Agent;
